import { box, type Aabb } from '../sim.js';
import { seededRng } from '../loot.js';
import { heightAt, type Heightfield } from '../terrain.js';

/**
 * Map-authoring helpers built on top of the AABB `box()` primitive from `sim.ts`.
 *
 * Physics note - three bands, and which one you author in is a design decision:
 *  - <= `STEP_UP` (0.5 m): a *step*. `stepPlayer`'s auto step-up rides the grounded capsule
 *    straight up onto the box, no jump and no mantle. This is the band for anything a player is
 *    meant to run up. Keep tread depth >= ~0.6 m so consecutive treads read as stairs.
 *  - `MANTLE_MIN_HEIGHT`..`MANTLE_MAX_HEIGHT` (0.5-1.4 m): a *mantle*. `stepPlayer` pulls the
 *    player up over ~0.2-0.4 s (see `MANTLE_RISE_PER_TICK`) when they are blocked horizontally
 *    with a ledge within `MANTLE_REACH` ahead. Deliberate, and slow enough to feel: use it for
 *    climbing routes, never for a flight of stairs. (Before 2026-09-04 there was no step-up and
 *    the mantle was an instant teleport, so map stairs were built out of mantle risers - which is
 *    exactly what "running up these stairs is super glitchy" was reporting.)
 *  - > `MANTLE_MAX_HEIGHT`: a wall. Nothing climbs it.
 */

/** A wall opening (doorway/breach). `center`/`width` run along the wall's own axis. */
export interface Opening {
  wall: 'n' | 's' | 'e' | 'w';
  center: number;
  width: number;
}

/** An axis-aligned rectangular hole cut out of a floor or roof slab. */
export interface Rect {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

/**
 * Subtracts `hole` from every rect in `rects`, splitting each intersected rect into the up-to-4
 * frame strips that remain around the hole (a standard rectangle-with-hole tessellation). Rects
 * that don't overlap the hole pass through unchanged. Used to carve interior openings (stairwells,
 * shafts, pits) out of an otherwise-solid floor/roof slab.
 */
function subtractRect(rects: Rect[], hole: Rect): Rect[] {
  const out: Rect[] = [];
  for (const r of rects) {
    const ix0 = Math.max(r.x0, hole.x0);
    const ix1 = Math.min(r.x1, hole.x1);
    const iz0 = Math.max(r.z0, hole.z0);
    const iz1 = Math.min(r.z1, hole.z1);
    if (ix0 >= ix1 || iz0 >= iz1) {
      out.push(r); // no overlap
      continue;
    }
    if (iz0 > r.z0) out.push({ x0: r.x0, x1: r.x1, z0: r.z0, z1: iz0 }); // north strip
    if (r.z1 > iz1) out.push({ x0: r.x0, x1: r.x1, z0: iz1, z1: r.z1 }); // south strip
    if (ix0 > r.x0) out.push({ x0: r.x0, x1: ix0, z0: iz0, z1: iz1 }); // west strip
    if (r.x1 > ix1) out.push({ x0: ix1, x1: r.x1, z0: iz0, z1: iz1 }); // east strip
  }
  return out;
}

const rectBox = (r: Rect, cy: number, sy: number): Aabb =>
  box((r.x0 + r.x1) / 2, cy, (r.z0 + r.z1) / 2, r.x1 - r.x0, sy, r.z1 - r.z0);

export interface SlabRoomOpts {
  /** Vertical offset added to every box, so rooms can stack as decks. Default 0. */
  yBase?: number;
  /** Emit the roof slab. Default true; pass false when the deck above supplies this deck's
   *  ceiling (its floor), so two decks stacked flush don't get two overlapping slabs. */
  roof?: boolean;
  /** Rectangular holes cut out of the floor slab (pits, shaft/stairwell openings). */
  floorHoles?: Rect[];
  /** Rectangular holes cut out of the roof slab (ignored when `roof` is false). */
  roofHoles?: Rect[];
}

/**
 * A hollow box room: a floor slab, an optional roof slab and four wall slabs, all of thickness
 * `wallT`. Interior spans y: yBase..yBase+h. `openings` cut gaps out of individual walls (e.g.
 * breaches, doorway alcoves); each wall is emitted as one segment per gap plus the remainders, so
 * a wall with an opening becomes 1-2 extra boxes. `floorHoles`/`roofHoles` do the same for the
 * floor/roof, via rectangle subtraction, so a shaft or stairwell can punch straight through.
 */
export function slabRoom(
  cx: number,
  cz: number,
  w: number,
  d: number,
  h: number,
  wallT: number,
  openings: Opening[] = [],
  opts: SlabRoomOpts = {},
): Aabb[] {
  const { yBase = 0, roof = true, floorHoles = [], roofHoles = [] } = opts;
  const boxes: Aabb[] = [];
  const x0 = cx - w / 2;
  const x1 = cx + w / 2;
  const z0 = cz - d / 2;
  const z1 = cz + d / 2;
  const full: Rect = { x0, x1, z0, z1 };

  const floorRects = floorHoles.reduce((rs, hole) => subtractRect(rs, hole), [full]);
  for (const r of floorRects) boxes.push(rectBox(r, yBase - wallT / 2, wallT)); // floor, top flush with yBase
  if (roof) {
    const roofRects = roofHoles.reduce((rs, hole) => subtractRect(rs, hole), [full]);
    for (const r of roofRects) boxes.push(rectBox(r, yBase + h + wallT / 2, wallT)); // roof, bottom flush with yBase+h
  }

  const walls: Record<Opening['wall'], { axis: 'x' | 'z'; fixed: number; span: [number, number] }> =
    {
      n: { axis: 'x', fixed: z0, span: [x0, x1] },
      s: { axis: 'x', fixed: z1, span: [x0, x1] },
      w: { axis: 'z', fixed: x0, span: [z0, z1] },
      e: { axis: 'z', fixed: x1, span: [z0, z1] },
    };

  for (const key of ['n', 's', 'e', 'w'] as const) {
    const spec = walls[key];
    const gaps = openings
      .filter((o) => o.wall === key)
      .map((o): [number, number] => [o.center - o.width / 2, o.center + o.width / 2])
      .sort((a, b) => a[0] - b[0]);

    let cursor = spec.span[0];
    const segments: [number, number][] = [];
    for (const [gs, ge] of gaps) {
      if (gs > cursor) segments.push([cursor, gs]);
      cursor = Math.max(cursor, ge);
    }
    if (cursor < spec.span[1]) segments.push([cursor, spec.span[1]]);

    for (const [s0, s1] of segments) {
      const len = s1 - s0;
      if (len <= 0.05) continue;
      const mid = (s0 + s1) / 2;
      if (spec.axis === 'x') boxes.push(box(mid, yBase + h / 2, spec.fixed, len, h, wallT));
      else boxes.push(box(spec.fixed, yBase + h / 2, mid, wallT, h, len));
    }
  }
  return boxes;
}

/**
 * A straight flight of stairs, each step a thin tread slice climbing `rise` m over `depth` m of
 * run. `dir` must be axis-aligned ({x:1,z:0}, {x:-1,z:0}, {x:0,z:1} or {x:0,z:-1}) - steps climb
 * away from (x,z) in that direction. `yBase` (default 0) offsets the whole flight vertically, so a
 * chain can start partway up another deck. Returns the treads plus the (x,y,z) of the top tread's
 * centre so callers can wire a waypoint node to the landing.
 */
export function stairs(
  x: number,
  z: number,
  dir: { x: number; z: number },
  steps: number,
  rise: number,
  depth: number,
  width: number,
  yBase = 0,
): { boxes: Aabb[]; top: { x: number; y: number; z: number } } {
  const dx = Math.sign(dir.x);
  const dz = Math.sign(dir.z);
  const boxes: Aabb[] = [];
  let top = { x, y: yBase, z };
  for (let i = 0; i < steps; i++) {
    const centerDist = depth * (i + 0.5);
    const cx = x + dx * centerDist;
    const cz = z + dz * centerDist;
    const yTop = yBase + rise * (i + 1);
    const yBot = yBase + rise * i;
    const sx = dx !== 0 ? depth : width;
    const sz = dz !== 0 ? depth : width;
    boxes.push(box(cx, (yTop + yBot) / 2, cz, sx, yTop - yBot, sz));
    top = { x: cx, y: yTop, z: cz };
  }
  return { boxes, top };
}

/** Square columns at every (x, z) combination, spanning yBase..yBase+height. */
export function pillarRow(
  xs: number[],
  zs: number[],
  height: number,
  size: number,
  yBase = 0,
): Aabb[] {
  const boxes: Aabb[] = [];
  for (const x of xs)
    for (const z of zs) boxes.push(box(x, yBase + height / 2, z, size, height, size));
  return boxes;
}

/** One footprint in a `terraces()` flight. */
export interface TerraceStep {
  cx: number;
  cz: number;
  w: number;
  d: number;
}

/**
 * Broad terrain terraces: each step is a solid block from the ground up to its own top, so
 * consecutive footprints should be nested/offset (each a bit smaller and set back further) to read
 * as a rising hillside. `rise` is the height gained per step.
 */
export function terraces(steps: TerraceStep[], rise: number): Aabb[] {
  return steps.map((s, i) => box(s.cx, (rise * (i + 1)) / 2, s.cz, s.w, rise * (i + 1), s.d));
}

/** Footprint + height range for one `scatter()` prop size. */
export interface ScatterSize {
  w: number;
  d: number;
  hMin: number;
  hMax: number;
}

/**
 * Seeded, deterministic scatter of `n` cover props inside `area`. Each prop's base sits at
 * `baseY(x, z)` (default flat 0) so props authored over sloped/heightfield terrain sit on it
 * rather than floating or burying - pass e.g. `(x, z) => heightAt(terrain, x, z)`.
 */
export function scatter(
  seed: number,
  n: number,
  area: { x0: number; z0: number; x1: number; z1: number },
  sizes: ScatterSize[],
  baseY: (x: number, z: number) => number = () => 0,
): Aabb[] {
  const rng = seededRng(seed);
  const boxes: Aabb[] = [];
  for (let i = 0; i < n; i++) {
    const size = sizes[Math.floor(rng() * sizes.length) % sizes.length]!;
    const x = area.x0 + rng() * (area.x1 - area.x0);
    const z = area.z0 + rng() * (area.z1 - area.z0);
    const h = size.hMin + rng() * (size.hMax - size.hMin);
    const base = baseY(x, z);
    boxes.push(box(x, base + h / 2, z, size.w, h, size.d));
  }
  return boxes;
}

/**
 * Height of the ground/terrace/platform directly beneath (x, z): the top of the tallest box whose
 * X/Z footprint contains the point, or the terrain height (if `terrain` is given) or `groundY`
 * (default 0) otherwise. Used to place every container, extract, spawn, hymn spawn and waypoint
 * node exactly on a walkable surface.
 *
 * Under an interior roof, a floor and its roof slab can share the same X/Z footprint at different
 * heights - pass `maxY` (the tallest surface a caller expects, e.g. just above a mezzanine but
 * below the roof) so the roof isn't mistaken for the floor.
 */
export function groundHeightAt(
  x: number,
  z: number,
  boxes: Aabb[],
  groundY = 0,
  maxY = Infinity,
  terrain?: Heightfield,
): number {
  const base = terrain ? heightAt(terrain, x, z) : groundY;
  // If even the terrain/flat baseline is above maxY (e.g. probing a sub-terrain feature such as a
  // pit floor), it can't be the answer - fall through to the box scan below instead of returning
  // a surface that violates the caller's cap.
  let y = base <= maxY ? base : -Infinity;
  for (const b of boxes) {
    if (x <= b.min.x || x >= b.max.x || z <= b.min.z || z >= b.max.z) continue;
    if (b.max.y > y && b.max.y <= maxY) y = b.max.y;
  }
  return y;
}
